import { Link } from "react-router-dom";
import { ArrowLeft, Scale, Building2, Server, Copyright, ShieldCheck, FileSignature } from "lucide-react";
import { Reveal } from "./Reveal";

const SECTIONS = [
  {
    icon: Building2,
    title: "Éditeur du site",
    content: [
      "Le site est édité par KLMProd, entreprise de DJ et d'animation musicale pour mariages, événements privés et séminaires.",
      "SIRET : 339 222 140 40065 · Code APE : 7021Z",
      "Contact : contact@klmprod.com",
      "Le directeur de la publication est le représentant légal de KLMProd.",
    ],
  },
  {
    icon: Server,
    title: "Hébergement",
    content: [
      "Le site est hébergé sur la plateforme Emergent (emergent.sh), infrastructure cloud sécurisée.",
      "Le nom de domaine est géré chez Hostinger.",
    ],
  },
  {
    icon: Copyright,
    title: "Propriété intellectuelle",
    content: [
      "L'ensemble des contenus du site (logo KLMProd, textes, visuels, structure) est la propriété exclusive de KLMProd, sauf mention contraire.",
      "Toute reproduction ou utilisation sans autorisation écrite préalable est interdite.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Données personnelles (RGPD)",
    content: [
      "Données collectées : formulaire de devis (nom, e-mail, téléphone, date et type d'événement, message), connexion Google (nom, e-mail, photo de profil), playlists collaboratives (titres proposés, prénom), fichiers déposés (diaporamas, photos, documents).",
      "Finalités : traitement des demandes de devis, organisation et préparation des prestations, gestion des paiements d'acompte et de solde.",
      "Base légale : exécution de mesures précontractuelles et du contrat. Aucune donnée n'est vendue ni partagée à des tiers à des fins commerciales.",
      "Sous-traitants techniques : Stripe (paiement), Resend (e-mails transactionnels), Emergent (hébergement et authentification Google).",
      "Durée de conservation : durée de la relation commerciale, puis archivage selon les obligations légales.",
      "Vos droits : accès, rectification, suppression et portabilité de vos données, en écrivant à contact@klmprod.com. Vous pouvez également saisir la CNIL (cnil.fr) en cas de litige.",
      "Cookies : seul un cookie technique de session (authentification) est utilisé ; aucun cookie publicitaire ou de suivi tiers n'est déposé par KLMProd.",
    ],
  },
  {
    icon: FileSignature,
    title: "Conditions de prestation",
    content: [
      "Toute prestation fait l'objet d'un devis et d'un contrat écrit détaillant horaires, matériel et options.",
      "Un acompte de 30 % est demandé à la signature du devis et du contrat ; le solde est réglable en ligne avant l'événement. Les paiements en ligne sont sécurisés par Stripe.",
      "Les demandes musicales via la playlist collaborative sont clôturées 48 heures avant l'événement.",
      "Les demandes de dernière minute (discours, surprises, diaporamas) doivent être transmises au plus tard 48 heures avant l'événement ; passé ce délai, elles sont traitées dans la mesure du possible, sans garantie.",
      "La coordination des surprises s'effectue exclusivement via deux contacts référents désignés par les clients.",
      "Les options (étincelles froides, fumée lourde, éclairage architectural, sonorisation de cérémonie, vidéoprojecteur…) font l'objet d'un devis dédié.",
      "La souscription d'une assurance annulation et intempéries est recommandée pour tout événement, notamment en extérieur.",
    ],
  },
];

export default function MentionsLegales() {
  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="legal-page">
      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-brand flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-cyan-400">
            <Scale size={14} /> Mentions légales & confidentialité
          </p>
          <h1 className="font-display mt-4 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
            Mentions <span className="text-gradient">légales.</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate-400">
            Informations légales, protection de vos données et conditions de prestation de KLMProd.
          </p>
        </Reveal>

        <div className="mt-12 space-y-6">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <section
                className="rounded-3xl border border-cyan-500/15 bg-[#0E1524]/70 p-7 backdrop-blur-xl"
                data-testid={`legal-section-${i + 1}`}
              >
                <h2 className="font-display flex items-center gap-3 text-lg font-bold text-slate-100">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10">
                    <s.icon size={17} className="text-cyan-300" />
                  </span>
                  {s.title}
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {s.content.map((line) => (
                    <li key={line.slice(0, 40)} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-400">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            </Reveal>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-slate-600" data-testid="legal-footer-note">
          Dernière mise à jour : septembre 2026 — KLMProd · SIRET 339 222 140 40065
        </p>
      </div>
    </div>
  );
}
